import { BehaviorSubject, EMPTY, from, fromEvent, merge, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, mergeMap, retry, share, startWith, switchMap, tap } from 'rxjs/operators';

import { authorizedFetch, readJson, postJson } from './http.js';

let hashConvId$ = fromEvent(window, 'hashchange').pipe(
    startWith(undefined),
    map(getConvIdFromHash),
);

function getConvIdFromHash() {
    let hash = window.location.hash || '';
    if (hash.startsWith('#')) {
        hash = hash.substring(1);
    }
    let prefix = 'conv/';
    if (!hash.startsWith(prefix)) {
        return null;
    }
    return hash.substring(prefix.length);
}

let loadedConvId = undefined;

let conversation$ = hashConvId$.pipe(
    filter(convId => convId !== loadedConvId),
    switchMap(convId => {
        loadedConvId = convId;
        if (!convId) {
            return of({
                id: null,
            });
        }
        return getConversation(convId);
    }),
    share({
        connector: () => new ReplaySubject(1),
        resetOnRefCountZero: true,
        resetOnComplete: false,
        resetOnError: true,
    }),
);

export let hasTitle$ = conversation$.pipe(
    map(conv => !!conv?.title),
);

let sessionId = new Date().getTime().toString(10+26);
let nextMessageId = 1;

let newChatMessage$ = new Subject();

export function appendChatMessage(msg) {
    newChatMessage$.next({
        ...msg,
        id: `${sessionId}-${nextMessageId++}`,
        created: new Date().toISOString(),
    });
}

let persistInput$ = new Subject();
let messageArchivedTrigger$ = new Subject();
let updateConvAttr$ = new Subject();

export let conversationTitle$ = conversation$.pipe(
    map(conv => conv?.title),
);

export let chatMessages$ = conversation$.pipe(
    switchMap(conv => {
        let messages = conv?.messages || {};
        let convId$ = of(conv?.id || null);
        let attrUpdater$ = updateConvAttr$.pipe(
            switchMap(attr => {
                let persist$ = of(undefined).pipe(
                    // convId$ must be switch mapped because it's
                    // instance can change over time
                    switchMap(() => convId$),
                    switchMap(convId => {
                        let convUpdate = {};
                        convUpdate[attr.key] = attr.value;
                        return appendConversation(convId, convUpdate);
                    }),
                );
                persistInput$.next(persist$);
                return EMPTY;
            }),
        );
        let messages$ = messageArchivedTrigger$.pipe(
            tap(archivedMessages => {
                if (archivedMessages.length === 0) {
                    return;
                }
                let persist$ = of(undefined).pipe(
                    // convId$ must be switch mapped because it's
                    // instance can change over time
                    switchMap(() => convId$),
                    switchMap(convId => {
                        let convUpdate = {
                            messages: {},
                        };
                        for (let msg of archivedMessages) {
                            convUpdate.messages[msg.id] = msg;
                        }
                        return appendConversation(convId, convUpdate);
                    }),
                );
                persistInput$.next(persist$);
            }),
            startWith(undefined),
            switchMap(() => {
                let latestMessage = null;
                for (let msgId of Object.keys(messages)) {
                    let msg = messages[msgId];
                    if (msg.archived) {
                        continue;
                    }
                    if (!latestMessage || msg.created > latestMessage.created) {
                        latestMessage = msg;
                    }
                }
                let chatMessages = [];
                for (let msg = latestMessage; msg; msg = messages[msg.formerId]) {
                    chatMessages.push(msg);
                }
                chatMessages.reverse();
                return newChatMessage$.pipe(
                    map(newMsg => {
                        let formerId = null;
                        if (chatMessages.length > 0) {
                            formerId = chatMessages[chatMessages.length - 1].id;
                        }
                        let newMsgToPersist = {
                            ...newMsg,
                            formerId,
                            created: new Date().toISOString(),
                        };
                        messages[newMsgToPersist.id] = newMsgToPersist;
                        chatMessages = [...chatMessages, newMsgToPersist];
                        let persist$ = of(undefined).pipe(
                            // convId$ must be switch mapped because it's
                            // instance can change over time
                            switchMap(() => convId$),
                            switchMap(convId => {
                                let convUpdate = {
                                    messages: {},
                                };
                                convUpdate.messages[newMsgToPersist.id] = newMsgToPersist;
                                if (convId) {
                                    return appendConversation(convId, convUpdate);
                                } else {
                                    convUpdate.created = new Date().toISOString();
                                    return createConversation(convUpdate).pipe(
                                        tap(convId => {
                                            convId$ = of(convId);
                                            loadedConvId = convId;
                                            window.location.hash = `#conv/${convId}`;
                                        }),
                                    );
                                }
                            }),
                        );
                        persistInput$.next(persist$);
                        return chatMessages;
                    }),
                    startWith(chatMessages),
                );
            }),
        );
        return merge(
            attrUpdater$,
            messages$,
        );
    }),
    share({
        connector: () => new ReplaySubject(1),
        resetOnError: true,
        resetOnComplete: true,
        resetOnRefCountZero: true,
    }),
);

/**
 * Updates a global conversation attribute.
 *
 * attr should be something like this:
 *
 * {
 *   key: 'title',
 *   value: 'ye title',
 * }
 */
export function updateConversationAttr(attr) {
    updateConvAttr$.next(attr);
}

/**
 * archives the chat message and everything afterwards.
 */
export function archiveMessage(msgId) {
    return chatMessages$.pipe(
        first(),
        map(messages => {
            let idx = messages.findIndex(msg => msg.id === msgId);
            if (idx === -1) {
                throw new Error(`Message with ID ${msgId} not found.`);
            }
            let archived = new Date().toISOString();
            let archivedMessages = [];
            for (let i = idx; i < messages.length; ++i) {
                let msg = messages[i];
                msg.archived = archived;
                archivedMessages.push(msg);
            }
            messageArchivedTrigger$.next(archivedMessages);
            return messages[idx];
        }),
    );
}

let persistQueueErrorChannel$ = new Subject();

let persistQueue$ = persistInput$.pipe(
    mergeMap(job$ => job$, 1),
    catchError(err => {
        persistQueueErrorChannel$.next(err);
        return throwError(err);
    }),
    // we use our own subscription to the persistQueue$. this will
    // make the persist queue continue saving data, even if the rest
    // of the application has crashed.
).subscribe();

function getConversation(convId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/conversation/${convId}`))),
        readJson(),
        conversationRetry(),
    );
}

function createConversation(conv) {
    return of(undefined).pipe(
        switchMap(() => postJson('/api/conversation', conv)),
        conversationRetry(),
    );
}

export function appendConversation(convId, update) {
    return of(undefined).pipe(
        switchMap(() => postJson(`/api/conversation/${convId}/append`, update)),
        conversationRetry(),
    );
}

function conversationRetry() {
    return retry({
        count: 3,
        delay: 1000 + 500*Math.random(),
    });
}

export let conversations$ = merge(
    persistQueueErrorChannel$,
);
